import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Checkbox,
  Link,
  Stack,
} from '@mui/material';
import { useDemoBotSelection } from '../../hooks/useDemoBotSelection';
import { ChainAvatar, chainName, chainOf, pairOf } from '../ui/ChainAvatar';
import { tokens, touchHitArea } from '../../theme';

interface BotSelectionModalProps {
  groupedBots: Record<string, string[]>;
}

/** Demo users pick the strategies they follow: grouped by chain, one row per strategy. */
export const BotSelectionModal: React.FC<BotSelectionModalProps> = ({ groupedBots }) => {
  const { availableBots, selectedBots, setSelectedBots, isBotSelectionOpen, closeBotSelection } = useDemoBotSelection();
  const [localSelectedBots, setLocalSelectedBots] = useState<string[]>(selectedBots);

  useEffect(() => {
    setLocalSelectedBots(selectedBots);
  }, [selectedBots, isBotSelectionOpen]);

  const byChain = useMemo(() => {
    const chains: Record<string, [string, string[]][]> = {};
    Object.entries(groupedBots)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([family, bots]) => {
        const chain = chainOf(family);
        (chains[chain] = chains[chain] || []).push([family, bots]);
      });
    return Object.entries(chains).sort(([a], [b]) => a.localeCompare(b));
  }, [groupedBots]);

  const familyCount = Object.keys(groupedBots).length;
  const selectedFamilies = Object.values(groupedBots).filter((bots) =>
    bots.some((botId) => localSelectedBots.includes(botId))
  ).length;

  const toggleFamily = (bots: string[], selected: boolean) => {
    setLocalSelectedBots((current) =>
      selected ? current.filter((bot) => !bots.includes(bot)) : [...new Set([...current, ...bots])]
    );
  };

  const handleSave = () => {
    setSelectedBots(localSelectedBots);
    closeBotSelection();
  };

  const textLink = (label: string, onClick: () => void, disabled = false) => (
    <Link
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      underline="none"
      sx={{
        position: 'relative',
        fontSize: '0.8125rem',
        fontWeight: 650,
        color: disabled ? 'text.disabled' : tokens.goldLight,
        cursor: disabled ? 'default' : 'pointer',
        ...touchHitArea,
      }}
    >
      {label}
    </Link>
  );

  return (
    <Dialog
      open={isBotSelectionOpen}
      onClose={closeBotSelection}
      aria-labelledby="bot-selection-modal-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="bot-selection-modal-title" sx={{ pb: 1 }}>
        Choose strategies
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          The cards follow your selection; the fund totals stay complete.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Stack direction="row" alignItems="center" spacing={2.5} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', flex: 1 }}>
            {selectedFamilies} of {familyCount} selected
          </Typography>
          {textLink('Select all', () => setLocalSelectedBots(availableBots), localSelectedBots.length === availableBots.length)}
          {textLink('Clear', () => setLocalSelectedBots([]), localSelectedBots.length === 0)}
        </Stack>

        <Stack spacing={2}>
          {byChain.map(([chain, families]) => (
            <Box key={chain} component="section" aria-label={chainName(chain)}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <ChainAvatar chain={chain} size={18} />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 650,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.6)',
                  }}
                >
                  {chainName(chain)}
                </Typography>
              </Stack>
              {families.map(([family, bots]) => {
                const count = bots.filter((botId) => localSelectedBots.includes(botId)).length;
                const all = count === bots.length;
                const some = count > 0 && !all;
                return (
                  <Box
                    key={family}
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minHeight: 48,
                      px: 0.5,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                    }}
                  >
                    <Checkbox
                      checked={all}
                      indeterminate={some}
                      onChange={() => toggleFamily(bots, all || some)}
                      inputProps={{ 'aria-label': `${pairOf(family)} on ${chainName(chain)}` }}
                    />
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, flex: 1 }}>{pairOf(family)}</Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', pr: 1 }}>
                      {bots.length} bot{bots.length === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeBotSelection}>Cancel</Button>
        <Button variant="contained" color="secondary" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
